/**
 * Unified script configuration pattern.
 * All scripts use a single interface `ScriptConfig` with a functional `src` signature:
 *   (baseSite: string, env: 'stg' | 'prod') => string
 * For static URLs, the function simply ignores its parameters.
 * This ensures consistent handling in the ScriptService (always invoke src(...)).
 */
export interface ScriptConfig {
  name: string;
  src: (baseSite: string, env: 'stg' | 'prod') => string; // Function to produce final URL; may ignore env/baseSite if not needed.
  baseSites: string[]; // markets where the script applies
  loaded?: boolean; // Future flag placeholders (keep shape extensible without per-script divergence):
}

export const ScriptStore: ScriptConfig[] = [
  {
    name: 'mikmak',
    src: () => '/scripts/mikmak.js',
    baseSites: ['hisense-de'],
  },
  {
    name: 'click2buy',
    src: () => 'https://widget.clic2buy.com/c2b.js',
    baseSites: ['hisense-fr'],
  },
  {
    name: '24ttlWtb',
    src: () => 'https://widget.24wtb.com/wtbWidget.js',
    baseSites: ['hisense-kz'],
  },
  // Consolidated 100Shoppers - dynamic subdomain based on market
  {
    name: '100HundredShoppers',
    src: (baseSite: string) => {
      const marketCode = baseSite.split('-')[1]; // Extract market code (pl, nl, be, at)
      const subdomainMap: Record<string, string> = {
        pl: '',
        nl: '-nl',
        be: '-be',
        at: '-at',
      };
      const subdomain = subdomainMap[marketCode] || '';
      return `https://widget${subdomain}.100shoppers.com/js/widget.min.js`;
    },
    baseSites: [
      'hisense-pl',
      'hisense-nl',
      'hisense-be',
      'hisense-at',
    ],
  },
  // Consolidated LiveChat - different URLs for CZ vs SK
  {
    name: 'livechat',
    src: (baseSite: string) => {
      const code = baseSite === 'hisense-sk' ? 'SK' : 'CZ';
      return `https://gorenje.secure.force.com/chat/resource/${code}HisenseChat`;
    },
    baseSites: ['hisense-cz', 'hisense-sk'],
  },
  // Consolidated BazaarVoice - handles stg/prod + market-specific paths
  {
    name: 'bazaarvoice',
    src: (baseSite: string, env: 'stg' | 'prod') => {
      const stage = env === 'prod' ? 'production' : 'staging';
      const configs: Record<string, { path: string; locale: string }> = {
        'hisense-uk': { path: 'hybris_site', locale: 'en_GB' },
        'hisense-de': { path: 'main_site', locale: 'de_DE' },
        'hisense-fr': { path: 'main_site', locale: 'fr_FR' },
      };
      const config = configs[baseSite];
      return `https://apps.bazaarvoice.com/deployments/${baseSite}/${config.path}/${stage}/${config.locale}/bv.js`;
    },
    baseSites: ['hisense-uk', 'hisense-de', 'hisense-fr'],
  },
  {
    name: 'jivochat',
    src: () => 'https://code.jivosite.com/widget/5M5OuILw2T',
    baseSites: ['hisense-ba'],
  },
  // Consolidated Trustpilot - same URL for all markets
  {
    name: 'trustpilot',
    src: () => 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
    baseSites: [
      'hisense-uk',
      'hisense-dk',
      'hisense-no',
      'hisense-se',
      'hisense-fi',
    ],
  },
  {
    name: 'kieskeurig',
    src: () => 'https://www.kieskeurig.nl/assets/wl-review.min.js',
    baseSites: ['hisense-nl', 'hisense-be'],
  },
  {
    name: 'dekovacka',
    src: () => 'https://app.dekovacka.cz/thankyou.js',
    baseSites: ['hisense-cz'],
  },
  {
    name: 'sportmonk-schedule',
    src: () => 'https://widgets.sportmonks.com/js/league/schedule.js',
    baseSites: ['hisense-de'],
  },
  {
    name: 'sportmonk-livescore',
    src: () => 'https://widgets.sportmonks.com/js/livescore/livescore.js',
    baseSites: ['hisense-de'],
  },
  {
    name: 'sportmonk-standings',
    src: () => 'https://widgets.sportmonks.com/js/tournament/standings.js',
    baseSites: ['hisense-de'],
  },
  {
    name: 'iAdvize',
    src: () => 'https://halc.iadvize.com/iadvize.js',
    baseSites: ['hisense-fr'],
  },
];
